import React, { useCallback, useEffect, useState } from 'react';
import { AnalyticsEventLocation } from '@mb/lib';
import type { Event } from '@mb/types';
import Arrow, { ArrowDirection } from 'components/Elements/Arrow/Arrow';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { useInView } from 'react-intersection-observer';
import { EventItem } from '../EventItem/EventItem';

export interface EventsCarrouselProps {
  events: Event[];
  title: string;
  isFeatured?: boolean;
  eventLocation: AnalyticsEventLocation | null;
  slideLimit?: number;
}

export const LIMIT_SLIDES = 12;

export const EventsCarrousel: React.FC<EventsCarrouselProps> = (props) => {
  const { events, title, isFeatured, slideLimit = LIMIT_SLIDES } = props;
  const [isInView, setIsInView] = useState(false);
  const [slidesInView, setSlidesInView] = useState<number[]>([]);
  const { ref: inViewRef } = useInView({
    threshold: 0.5,
    onChange: setIsInView,
  });
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: true,
    slidesToScroll: 'auto',
  });
  // @ts-expect-error // TODO: fix types
  const onSelect = useCallback((emblaApi) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  const updateSlidesInView = useCallback((emblaApi: EmblaCarouselType) => {
    setSlidesInView((slidesInView) => {
      if (slidesInView.length === emblaApi.slideNodes().length) {
        emblaApi.off('slidesInView', updateSlidesInView);
      }
      const inView = emblaApi
        .slidesInView()
        .filter((index: number) => !slidesInView.includes(index));
      return slidesInView.concat(inView);
    });
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    updateSlidesInView(emblaApi);
    emblaApi.on('slidesInView', updateSlidesInView);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect, updateSlidesInView]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      /*sendCarouselAnalyticsEvent(
                title,
                CarouselAction.BACK,
                eventLocation
            );*/
      emblaApi.scrollPrev();
    }
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      /*sendCarouselAnalyticsEvent(
                title,
                CarouselAction.FORWARD,
                eventLocation
            );*/
      emblaApi.scrollNext();
    }
  }, [emblaApi]);

  if (events.length === 0) return null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center p-0">
        <h2 className="font-bold text-lgg">{title}</h2>
      </div>
      <div className="relative">
        {slideLimit > 2 && (
          <Arrow
            direction={ArrowDirection.LEFT}
            onClick={scrollPrev}
            show={!prevBtnDisabled}
          />
        )}
        {slideLimit > 2 && (
          <Arrow
            direction={ArrowDirection.RIGHT}
            onClick={scrollNext}
            show={!nextBtnDisabled}
          />
        )}
        <div className="relaive w-full flex overflow-hidden" ref={emblaRef}>
          <ul className="relative w-full flex flex-nowrap touch-pan-y list-none -mx-2 px-2 sm:gap-4">
            {events.slice(0, slideLimit).map((event, index) => (
              <EventItem
                key={`event-${index}`}
                event={event}
                index={index}
                isFeatured={isFeatured}
                inView={slidesInView.indexOf(index) > -1}
              />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
